/* eslint-disable */
import isEmail from 'validator/lib/isEmail';
import isCreditCard from 'validator/lib/isCreditCard';
import * as yup from 'yup';
import {
  Country,
  CreditCardType,
  DonationField,
  ErrorMessages,
  RegularExpressions,
} from '../constants';
import { detectCreditCardType } from './detectCreditCard';

const regExHelper = (val: string, expression: any, flags = ''): boolean => {
  const regex = new RegExp(expression, flags);
  const matches = regex.test(val);
  return matches;
};

const MIN_DONATION_AMOUNT = 5;
const MAX_DONATION_AMOUNT = 2000000;

const isCreditCardPresent = (val: string) => val.trim() !== '';

/**
 * @description Checks that the provided amount is  valid currency amount. Max of 2 decimal places permitted.
 */
export const isValidCurrencyAmount = (val: string): boolean =>
  regExHelper(val, RegularExpressions.isValidOtherAmount);

const isMinimumDonationAmount = (val: string): boolean =>
  parseFloat(val) >= MIN_DONATION_AMOUNT;
const isMaximumDonationAmount = (val: string): boolean =>
  parseFloat(val) <= MAX_DONATION_AMOUNT;

export const isNumeric = (val: string): boolean =>
  regExHelper(val, RegularExpressions.isNumeric);

export const isAlphabetic = (val: string): boolean =>
  regExHelper(val, RegularExpressions.isAlphabetic);

export const isEmailAddress = (val: string): boolean => isEmail(val);

export const isValidCreditCard = (val: string): boolean =>
  isCreditCard(val) || val.trim() === '';

export const isDonationTooLow = (val: string): boolean => parseFloat(val) < 5;

export const isDonationTooHigh = (val: string): boolean =>
  parseFloat(val) > MAX_DONATION_AMOUNT;

const getCurrentYear = () => new Date().getFullYear() % 100;

export const isCreditCardMonthValid = (month: string): boolean =>
  parseInt(month) >= 1 && parseInt(month) <= 12;

export const isCreditCardExpirationWithinTenYears = (year: string): boolean =>
  parseInt(year) <= getCurrentYear() + 10;

export const isCreditCardExpirationYearFuture = (year: string): boolean =>
  parseInt(year) >= getCurrentYear();

export const isValidFirstNameOrLastName = (val: string): boolean =>
  regExHelper(val, RegularExpressions.isValidNameOrLastName);

export const hasAtLeastOneChar = (val: string): boolean =>
  regExHelper(val, RegularExpressions.hasAtLeastOneChar);

export const isValidUSZipCode = (val: string): boolean =>
  regExHelper(val, RegularExpressions.isValidUSZipCode);

export const isValidCanadianPostCode = (val: string): boolean =>
  regExHelper(val, RegularExpressions.isValidCanadianPostCode, 'i');

export const isValidInternationalPostCode = (val: string): boolean =>
  regExHelper(val, RegularExpressions.isValidInternationalPostCode);

export const hasSpecialCharactersNotAllowed = (val = ''): boolean =>
  regExHelper(val, RegularExpressions.hasSpecialCharactersNotAllowed);

export const isValidCity = (val: string): boolean =>
  !regExHelper(val, RegularExpressions.hasOnlyNumbers);

/**
 * @description Validate that the expiration month represents an appropriate future date.
 *  This depends on the year. For future years we don't care what the month is, it's in the future.
 *  For the current year, make sure the month is at least the current month.
 * @param year
 * @param month
 * @returns
 */
export const isCreditCardExpirationMonthFuture = (
  year: string,
  month: string
): boolean => {
  /* If a year has not been provided yet, then don't attempt to validate the expiration date until we have
   * both a month and a year.
   */
  if (year.trim() === '') return true;

  // If a year is in the past then let the past year take precedence and avoid doubling up on errors. */
  if (parseInt(year) < getCurrentYear()) return true;

  const currentMonth = new Date().getMonth() + 1;
  const cardMonth = parseInt(month);
  const cardYear = parseInt(year);

  return (
    (cardYear === getCurrentYear() && cardMonth >= currentMonth) ||
    cardYear > getCurrentYear()
  );
};

export const isValidCVV = (cvv: string, context?: any): boolean => {
  const trimCVV = cvv.trim();
  const cardType = detectCreditCardType(
    context?.parent?.creditCardNumber || ''
  );
  return cardType !== CreditCardType.AmericanExpress
    ? isNumeric(trimCVV) && trimCVV.length === 3
    : true;
};

export const isValidAmex = (cvv: string, context?: any): boolean => {
  const trimCVV = cvv.trim();
  const cardType = detectCreditCardType(
    context?.parent?.creditCardNumber || ''
  );
  return cardType === CreditCardType.AmericanExpress
    ? isNumeric(trimCVV) && trimCVV.length > 3 && trimCVV.length <= 4
    : true;
};

/**
 * @description Builds the yup donation form validation schema.
 *   All the error messages and validation rules are encapsulated here.
 */
export const buildFormSchema = ({
  isOtherDonation,
}: {
  isOtherDonation: boolean;
}) => {
  const schema: any = {};

  schema[DonationField.donationAmount] = yup.number();
  /* Donation Amount. Only validated if selected */
  if (isOtherDonation) {
    schema[DonationField.donationOtherAmount] = yup
      .string()
      .required(ErrorMessages.donationAmountRequired)
      .test(
        'isValidAmount',
        ErrorMessages.donationAmountInvalid,
        isValidCurrencyAmount
      )
      .test(
        'minimumAmount',
        ErrorMessages.donationAmountTooLow,
        isMinimumDonationAmount
      )
      .test(
        'maximumAmount',
        ErrorMessages.donationAmountTooHigh,
        isMaximumDonationAmount
      );
  }

  /* Your Info */
  schema[DonationField.country] = yup
    .string()
    .required(ErrorMessages.countryRequired);

  schema[DonationField.firstName] = yup
    .string()
    .required(ErrorMessages.firstNameRequired)
    .test(
      'hasAtLeastOneChar',
      ErrorMessages.firstNameHasAtLeastOneChar,
      hasAtLeastOneChar
    )
    .test(
      'isValidFirstName',
      ErrorMessages.firstNameInvalid,
      isValidFirstNameOrLastName
    );

  schema[DonationField.lastName] = yup
    .string()
    .required(ErrorMessages.lastNameRequired)
    .test(
      'hasAtLeastOneChar',
      ErrorMessages.lastNameHasAtLeastOneChar,
      hasAtLeastOneChar
    )
    .test(
      'isValidLastName',
      ErrorMessages.lastNameInvalid,
      isValidFirstNameOrLastName
    );

  schema[DonationField.donorEmail] = yup
    .string()
    .required(ErrorMessages.emailRequired)
    .test('isValidEmail', ErrorMessages.emailInvalid, isEmailAddress);

  /* Billing Info */
  schema[DonationField.address1] = yup
    .string()
    .required(ErrorMessages.address1Required)
    .test(
      'hasSpecialCharactersNotAllowed',
      ErrorMessages.hasSpecialCharactersNotAllowed,
      hasSpecialCharactersNotAllowed
    );

  schema[DonationField.address2] = yup
    .string()
    .test(
      'hasSpecialCharactersNotAllowed',
      ErrorMessages.hasSpecialCharactersNotAllowed,
      hasSpecialCharactersNotAllowed
    );

  schema[DonationField.address3] = yup
    .string()
    .test(
      'hasSpecialCharactersNotAllowed',
      ErrorMessages.hasSpecialCharactersNotAllowed,
      hasSpecialCharactersNotAllowed
    );

  schema[DonationField.city] = yup
    .string()
    .required(ErrorMessages.cityRequired)
    .test(
      'hasSpecialCharactersNotAllowed',
      ErrorMessages.hasSpecialCharactersNotAllowed,
      hasSpecialCharactersNotAllowed
    )
    .test('cityHasOnlyNumbers', ErrorMessages.cityRequired, isValidCity);

  schema[DonationField.state] = yup
    .string()
    .when([DonationField.country], (country: string[], _schema) => {
      if (country[0] === Country.Canada) {
        return _schema.required(ErrorMessages.provinceRequired);
      }

      return _schema.required(ErrorMessages.stateRequired);
    })
    .test(
      'hasSpecialCharactersNotAllowed',
      ErrorMessages.hasSpecialCharactersNotAllowed,
      hasSpecialCharactersNotAllowed
    );

  schema[DonationField.postCode] = yup
    .string()
    .when([DonationField.country], (country: string[], _schema) => {
      if (country[0] === Country.UnitedStates) {
        return _schema
          .required(ErrorMessages.zipCodeRequired)
          .test(
            'isValidUSZipCode',
            ErrorMessages.zipCodeInvalid,
            (zipCode: string) => isValidUSZipCode(zipCode)
          );
      }

      if (country[0] === Country.Canada) {
        return _schema
          .required(ErrorMessages.postCodeRequired)
          .test(
            'isValidCanadianPostCode',
            ErrorMessages.postCodeInvalid,
            (postCode: string) => isValidCanadianPostCode(postCode)
          );
      }

      return _schema
        .required(ErrorMessages.postCodeRequired)
        .test(
          'isValidInternationalPostCode',
          ErrorMessages.internationalPostCodeInvalid,
          (countrySelected: string) =>
            isValidInternationalPostCode(countrySelected)
        );
    });

  return yup.object(schema);
};
